import React from 'react';

import { useIntl } from 'react-intl';
import classNames from 'classnames';

import CreditButton from '@/components/shared/bonus-card/CreditButton';
import CashierButton from '@/components/shared/bonus-card/CashierButton';
import ForfeitButton from '@/components/shared/bonus-card/ForfeitButton';
import { isEmpty } from '@/shared/utility';
import { cdnBonusImg } from '@/utils/image-utils';
import { GigBonusRedeemType } from '@lucky7ventures/bff-types';

interface BonusCardProps {
  // Props for both available and active bonuses
  id: string;
  desc: string | null;
  heading: string;
  bonusCode: string | null;
  redeemTypeId: GigBonusRedeemType | null;
  availableUntilText: string | null;
  ctaText: string | null;
  ctaOnAction?: () => void;
  image: string | null;
  title: string;
  // Props for active bonuses
  userBonusId: number | null;
  bonusMoney: number | null;
  realBonusMoney: number | null;
  // General props
  className?: string;
}

function BonusCard({
  bonusCode,
  id,
  heading,
  image,
  desc,
  title,
  redeemTypeId,
  availableUntilText,
  userBonusId,
  realBonusMoney,
  bonusMoney,
  className,
  ctaText,
  ctaOnAction,
}: BonusCardProps) {
  const intl = useIntl();

  return (
    <>
      <div
        className={classNames(
          `rounded-md bg-white m-4 relative overflow-hidden max-w-[350px] min-w-[250px] w-full
          pt-12 px-8 pb-4 flex flex-col flex-wrap justify-start flex-1 sm:-mt-16 sm:mb-[80px]`,
          className,
        )}
        data-cy="bonus-card"
      >
        <div className="absolute -top-[20px] left-0 sm:-top-[100px]" id={id} />
        <div className="relative flex justify-between text-center flex-col flex-wrap flex-1">
          <div className="text">
            <p className="text-emerald-600 font-bold text-sm rounded-[50vh] inline-block justify-end mb-4">
              {title}
            </p>
            <h3 className="mt-0 text-gray-900 text-[22px] mb-4">{heading}</h3>
            <div className="mb-0 italic font-bold opacity-80">
              {!isEmpty(desc) && <p className="description">{desc}</p>}
              {!isEmpty(availableUntilText) && (
                <p className="bonusExpiration">
                  {intl.formatMessage({ id: 'bonusCard.availableUntil' })}: <br />
                  {availableUntilText}
                </p>
              )}
            </div>
            {image !== null && (
              <img
                src={cdnBonusImg(image, {
                  width: '180',
                  height: 'auto',
                  dpr: '2',
                  fit: 'contain',
                })}
                alt={image}
                className="w-full max-w-[90px] mx-auto mb-2.5 h-auto"
              />
            )}
          </div>
          <div className="flex justify-center flex-wrap mt-2.5">
            {redeemTypeId === GigBonusRedeemType.BonusCode && bonusCode && (
              <CreditButton promoCode={bonusCode} />
            )}
            {(redeemTypeId === GigBonusRedeemType.DepositBonusCode ||
              redeemTypeId === GigBonusRedeemType.Deposit) &&
              bonusCode && <CashierButton promoCode={bonusCode} />}
            {redeemTypeId === GigBonusRedeemType.ForfeitBonus && (
              <ForfeitButton
                bonusMoney={bonusMoney!}
                realBonusMoney={realBonusMoney!}
                userBonusId={userBonusId!}
                bonusName={heading}
              />
            )}
            <span
              className="text-black bg-none text-sm font-bold m-0 p-4 transition-opacity duration-200 ease-in-out
              block w-full no-underline opacity-60 hover:opacity-100 cursor-pointer"
              onClick={ctaOnAction}
              data-cy="bonus-card-bonus-terms"
            >
              {ctaText}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

export default BonusCard;
